import { toast } from '@/hooks/use-toast';
import { getStudyTagLinks, putStudyTagLinks } from '@/lib/api/study-tags';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
const studyTagsKey = (studyId) => ['study-tags', studyId];
/** The study's own tag links (owner clinic only: a shared-in study is never fetched). */
export function useStudyTags(studyId, enabled) {
    return useQuery({
        queryKey: studyTagsKey(studyId),
        queryFn: () => getStudyTagLinks(studyId),
        enabled,
        staleTime: 30_000,
    });
}
function toInputs(drafts) {
    return drafts.map((d) => d.tagId
        ? { tagId: d.tagId, qualifier: d.qualifier }
        : { newTag: d.newTag ?? { label: d.label }, qualifier: d.qualifier });
}
/**
 * Replace a study's tag set. Each add/remove/qualifier change is a discrete
 * action, so it saves at once: the links cache is updated optimistically (and
 * rolled back on failure), then the worklist rows and the sidebar's tag counts
 * are refetched, since both now include the study's tags.
 */
export function useReplaceStudyTags(studyId) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const key = studyTagsKey(studyId);
    return useMutation({
        mutationKey: key,
        mutationFn: (drafts) => putStudyTagLinks(studyId, toInputs(drafts)),
        onMutate: async (drafts) => {
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData(key);
            queryClient.setQueryData(key, drafts.map((d) => {
                const existing = d.tagId ? previous?.find((p) => p.tagId === d.tagId) : undefined;
                return {
                    id: existing?.id ?? `pending:${d.label.toLowerCase()}`,
                    studyId,
                    tagId: d.tagId ?? `new:${d.label.toLowerCase()}`,
                    label: d.label,
                    normalizedLabel: d.label.toLowerCase(),
                    aliases: existing?.aliases ?? d.newTag?.aliases ?? [],
                    category: existing?.category ?? d.newTag?.category ?? null,
                    qualifier: d.qualifier,
                    addedBy: existing?.addedBy ?? '',
                    addedAt: existing?.addedAt ?? new Date().toISOString(),
                };
            }));
            return { previous };
        },
        onSuccess: (links) => {
            // A later edit already in flight owns the cache (its optimistic set is
            // newer than this response); it writes its own answer when it lands.
            if (queryClient.isMutating({ mutationKey: key }) <= 1)
                queryClient.setQueryData(key, links);
        },
        onError: (_error, _drafts, context) => {
            queryClient.setQueryData(key, context?.previous);
            toast({ title: t('worklist.studyTags.saveFailed'), variant: 'destructive' });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ['studies'] });
            void queryClient.invalidateQueries({ queryKey: ['report-tag-usage'] });
        },
    });
}
